export type DownloadPlatform = "macos" | "windows";
export type DownloadArch = "universal" | "x86_64" | "aarch64";

export interface DownloadItem {
  platform: DownloadPlatform;
  arch: DownloadArch;
  label: string;
  url: string;
  size: number;
  sha256: string;
  minimumOs: string;
}

export interface DownloadManifest {
  version: string;
  publishedAt: string;
  downloads: DownloadItem[];
}

export interface NavigatorLike {
  userAgent?: string;
  platform?: string;
  userAgentData?: {
    getHighEntropyValues?: (hints: string[]) => Promise<{ architecture?: string; bitness?: string }>;
  };
}

export const FALLBACK_MANIFEST: DownloadManifest = {
  version: "1.1.36",
  publishedAt: "2026-08-07T13:39:10.000Z",
  downloads: [
    {
      platform: "macos",
      arch: "universal",
      label: "macOS 通用版",
      url: "/downloads/1.1.36/SFast-1.1.36-macos-universal.dmg",
      size: 90115430,
      sha256: "3c5e06a5d22215d57802cbba50671e12379835ef25b56d7eb7615de8620f2bf5",
      minimumOs: "macOS 12 或更高版本"
    },
    {
      platform: "windows",
      arch: "x86_64",
      label: "Windows x64",
      url: "/downloads/1.1.36/SFast-1.1.36-windows-x64-setup.exe",
      size: 24681086,
      sha256: "c3e4c8ae8bb4b81a548ad6ddc9a38f305dcc599e79f773330a76d4b3a1dc3d02",
      minimumOs: "64 位 Windows 10/11"
    },
    {
      platform: "windows",
      arch: "aarch64",
      label: "Windows ARM64",
      url: "/downloads/1.1.36/SFast-1.1.36-windows-arm64-setup.exe",
      size: 20800404,
      sha256: "51559875cfc0186e5566be4c9c02a00342f6e7f46ad826c0d1298df27f41be85",
      minimumOs: "ARM64 Windows 10/11"
    }
  ]
};

export function formatBytes(bytes: number): string {
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export function isDownloadManifest(value: unknown): value is DownloadManifest {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Partial<DownloadManifest>;
  return Boolean(
    typeof candidate.version === "string" &&
      /^\d+\.\d+\.\d+$/.test(candidate.version) &&
      Array.isArray(candidate.downloads) &&
      candidate.downloads.length === 3 &&
      candidate.downloads.every((item) => {
        if (!item || typeof item !== "object") return false;
        const download = item as Partial<DownloadItem>;
        return (
          ["macos", "windows"].includes(download.platform || "") &&
          ["universal", "x86_64", "aarch64"].includes(download.arch || "") &&
          typeof download.url === "string" &&
          download.url.startsWith("/downloads/") &&
          Number.isFinite(download.size) &&
          typeof download.sha256 === "string" &&
          /^[a-f0-9]{64}$/.test(download.sha256)
        );
      })
  );
}

export function recommendDownload(downloads: DownloadItem[], navigatorLike: NavigatorLike = {}): DownloadItem {
  const userAgent = String(navigatorLike.userAgent || "").toLowerCase();
  const platform = String(navigatorLike.platform || "").toLowerCase();
  const isMac = userAgent.includes("mac") || platform.includes("mac");
  if (isMac) return downloads.find((item) => item.platform === "macos") || downloads[0];

  const isArm = /arm64|aarch64/.test(userAgent) || /arm64|aarch64/.test(platform);
  if (isArm) {
    return downloads.find((item) => item.platform === "windows" && item.arch === "aarch64") || downloads[0];
  }
  return downloads.find((item) => item.platform === "windows" && item.arch === "x86_64") || downloads[0];
}

export async function detectRecommendedDownload(downloads: DownloadItem[], navigatorLike: NavigatorLike = {}): Promise<DownloadItem> {
  const basic = recommendDownload(downloads, navigatorLike);
  if (basic.platform !== "windows") return basic;
  const getHighEntropyValues = navigatorLike.userAgentData?.getHighEntropyValues;
  if (!getHighEntropyValues) return basic;
  try {
    const details = await getHighEntropyValues(["architecture", "bitness"]);
    if (/arm/i.test(details.architecture || "")) {
      return downloads.find((item) => item.platform === "windows" && item.arch === "aarch64") || basic;
    }
  } catch {
    return basic;
  }
  return basic;
}
